import type { User } from '@/features/session/types/session.types';
import Papa from 'papaparse';

const decodeCsvContent = (rawText: string): string => {
  let current = rawText;

  // Decode at most a few layers when payload arrives JSON-wrapped.
  for (let i = 0; i < 3; i += 1) {
    const trimmed = current.trim();

    try {
      if (trimmed.startsWith('{') && trimmed.includes('"csv_content"')) {
        const parsed = JSON.parse(trimmed) as { csv_content?: unknown };
        if (typeof parsed.csv_content === 'string') {
          current = parsed.csv_content;
          continue;
        }
      }

      if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed === 'string') {
          current = parsed;
          continue;
        }
      }
    } catch {
      break;
    }

    break;
  }

  return current;
};

const sanitizeCell = (value: string): string => {
  let sanitized = value;

  for (let i = 0; i < 3; i += 1) {
    const next = sanitized
      .replace(/\\r\\n/g, '\n')
      .replace(/\\r/g, '')
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '')
      .replace(/\t/g, '');

    if (next === sanitized) {
      break;
    }

    sanitized = next;
  }

  return sanitized;
};

const canonicalizeCsvContent = (rawCsvText: string): string => {
  const parsed = Papa.parse<string[]>(rawCsvText, {
    skipEmptyLines: false,
  });

  if (!parsed.data || parsed.data.length === 0) {
    return rawCsvText;
  }

  const cleanedRows = parsed.data.map(row =>
    row.map(cell => sanitizeCell(cell))
  );

  return Papa.unparse(cleanedRows, {
    newline: '\n',
    skipEmptyLines: false,
  });
};

export const extractCsvContentAsJsonString = async (
  file: File,
  user: User | null
): Promise<FormData> => {
  const userName =
    user && user.givenName && user.familyName
      ? `${user.givenName} ${user.familyName}`
      : user?.email || 'Unknown';
  const normalizedCsvText = canonicalizeCsvContent(
    decodeCsvContent(await file.text())
  );

  const formData = new FormData();
  formData.append('csv_content', normalizedCsvText);
  formData.append('file_name', file.name);
  formData.append('original_file_name', file.name);
  formData.append('uploaded_by', userName);
  formData.append('content_type', file.type || 'text/csv');

  return formData;
};

const looksLikeCsv = (value: string): boolean => {
  const normalized = value.trim();
  return normalized.includes(',') && /\r?\n/.test(normalized);
};

const extractCsvFromObject = (value: unknown): string | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const data = value as Record<string, unknown>;
  const candidates = [
    data.errorCsv,
    data.error_csv,
    data.csv,
    data.content,
    data.result,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && looksLikeCsv(candidate)) {
      return candidate;
    }
  }

  return null;
};

export const extractCsvResponse = async (
  responseData: unknown,
  contentType = ''
): Promise<string | null> => {
  if (typeof responseData === 'string') {
    if (contentType.includes('text/csv') || looksLikeCsv(responseData)) {
      return responseData;
    }
    return null;
  }

  if (typeof Blob !== 'undefined' && responseData instanceof Blob) {
    const text = await responseData.text();
    if (contentType.includes('text/csv') || looksLikeCsv(text)) {
      return text;
    }
    return null;
  }

  if (responseData instanceof ArrayBuffer) {
    const text = new TextDecoder().decode(responseData);
    if (contentType.includes('text/csv') || looksLikeCsv(text)) {
      return text;
    }
    return null;
  }

  return extractCsvFromObject(responseData);
};

export const csvHasInvalidRows = (csv: string): boolean =>
  /,\s*INVALID\s*,/i.test(csv) || /\n[^\n]*,\s*INVALID\s*,/i.test(csv);
